<template>
  <div>
    <b-container>
      <div class="mt-4 d-flex align-items-start justify-content-between burger-spacing">
        <back-arrow :title="project.tostring" :route-back="'ProjectIndex'"/>

        <show-actions
          :duplicate="allowDuplicate"
          :boost="allowBoost"
          :unboost="allowUnboost"
          :editItem="allowEdit"
          :deleteItem="allowDelete"
          :deleteForever="allowDeleteForever"
          @clickOnDuplicateBtn="clickOnDuplicateBtn"
          @clickOnBoostBtn="clickOnBoostBtn"
          @clickOnEditBtn="clickOnEditBtn"
          @clickOnDeleteBtn="clickOnDeleteBtn">
        </show-actions>

      </div>

      <!-- alert pour le status -->
        <project-status-alert-component
          :project="project"
          v-model="project">
        </project-status-alert-component>

      <!-- cadre des demandes de modification d'un porteur sur un projet publié pour validation/refus par les commissions/admins -->
      <div v-if="showModifiedFields">
        <div class="page-title mt-4">
          {{$t('project.ModifiedFields')}}
        </div>

        <div class="row mt-2">
          <div class="col-md-12">
            <b-card class="mt-2 mb-4 shadow-sm">
              <div class="row">

                <!-- nom modifié -->
                <div class="col-md-12 mb-5" v-if="project && project.nameModified">
                  <show-value-field
                    :label="$t('project.Name')"
                    :object="project"
                    :property="'nameModified'">
                  </show-value-field>
                </div>

                <!-- description modifiée -->
                <div class="col-md-12 mb-5" v-if="project && project.descriptionModified">
                  <show-rich-text-value-field
                    :label="$t('project.Description')"
                    :object="project"
                    :property="'descriptionModified'">
                  </show-rich-text-value-field>
                </div>

                <!-- image modifiée -->
                <div class="col-md-6 mb-5" v-if="project && project.projectImageModified">
                  <show-image-field
                    :label="$t('project.ProjectImage')"
                    :src="project.projectImageModified? project.projectImageModified.fileUrl: null">
                  </show-image-field>
                </div>

                <!-- boutons de validation ou de refus -->
                <div class="col-md-12 d-flex justify-content-end mt-4" v-if="!$store.getters.isUser" >
                  <b-button variant="primary" @click="refuseModifications">
                    <span v-if="!isLoadingRefuse">{{ $t('Refuse.modifications') }}</span>
                    <b-spinner v-else :label="$t('spinner.isLoading')" small></b-spinner>
                  </b-button>
                  <b-button variant="secondary" class="ml-3" @click="acceptModifications">
                    <span v-if="!isLoadingAccept">{{ $t('Accept.modifications') }}</span>
                    <b-spinner v-else :label="$t('spinner.isLoading')" small></b-spinner>
                  </b-button>
                </div>

              </div>
            </b-card>
          </div>
        </div>
      </div>

      <!-- cadre des champs de modification réservés aux commissions/admins -->
      <div v-if="!$store.getters.isUser">
        <div class="page-title mt-4">
          {{$t('project.Infos') + ' ' + $t('Commission')}}
        </div>

        <div class="row mt-2">
          <div class="col-md-12">
            <b-card class="mt-2 mb-4 shadow-sm">
              <div class="row">

                <!-- nom du porteur -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-object-link-value-field
                    :label="$t('project.Creator')"
                    :object="project.creator"
                    :route="'/user/show/'">
                  </show-object-link-value-field>
                </div>

                <!-- ID du projet -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-value-field
                    :label="$t('project.ID')"
                    :object="project"
                    :property="'id'">
                  </show-value-field>
                </div>

                <!-- statut du projet -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-value-field
                    :label="$t('ProjectStatus')"
                    :object="project.projectStatus"
                    :property="'text'"
                    :need-translation="true">
                  </show-value-field>
                </div>

                <!-- date de soumission -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-date-field
                    :label="$t('project.SubmissionDate')"
                    :object="project"
                    :property="'submissionDate'">
                  </show-date-field>
                </div>

                <!-- date de certification -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-date-field
                    :label="$t('project.CertificationDate')"
                    :object="project"
                    :property="'certificationDate'">
                  </show-date-field>
                </div>

                <!-- date de publication -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-date-field
                    :label="$t('project.PublicationDate')"
                    :object="project"
                    :property="'publicationDate'">
                  </show-date-field>
                </div>

                <!-- intéressant pour le serice communication -->
                <div class="col-md-12 mb-5">
                  <show-boolean-field
                    :label="$t('project.IsComRelevant')"
                    :object="project"
                    :property="'isComRelevant'">
                  </show-boolean-field>
                </div>

                <!-- budget demandé -->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-boolean-field
                    :label="$t('project.BudgetNeeded')"
                    :object="project"
                    :property="'budgetNeeded'">
                  </show-boolean-field>
                </div>

                <!-- montant du budget-->
                <div class="col-md-6 col-sm-12 mb-5">
                  <show-value-field
                    :label="$t('project.Budget')"
                    :object="project"
                    :property="'budget'"
                    :text-after="$t('defaultCurrency')">
                  </show-value-field>
                </div>

                <!-- commentaire du porteur -->
                <div class="col-md-12 mb-5">
                  <div class="section-title mb-2">{{$t('project.UserComment')}}</div>
                  <show-value-field
                    :display-label="false"
                    :object="project"
                    :property="'userComment'">
                  </show-value-field>
                </div>

                <!-- commentaire de la commission -->
                <div class="col-md-12 mb-5">
                  <div class="section-title mb-2">{{$t('project.CommissionComment')}}</div>
                  <show-value-field
                    :display-label="false"
                    :object="project"
                    :property="'commissionComment'">
                  </show-value-field>
                </div>

                <!-- message au porteur -->
                <div class="col-md-12" v-if="project && project.message">
                  <div class="section-title mb-2">{{$t('project.Message')}}</div>
                  <show-value-field
                    :display-label="false"
                    :object="project"
                    :property="'message'">
                  </show-value-field>
                </div>

              </div>
            </b-card>
          </div>
        </div>
      </div>

      <div class="page-title mt-4">
        {{$t('project.Infos') + ' ' + $t('Project')}}
      </div>

      <div class="row">
        <div class="col-md-12">
          <b-card class="mt-2 mb-4 shadow-sm">
            <div class="row">

              <!-- image -->
              <div class="col-md-12 mb-4">
                <show-image-field
                  v-if="project.projectImage"
                  :display-label="false"
                  :src="project.projectImage? project.projectImage.fileUrl: null">
                </show-image-field>
              </div>

              <!-- types d'évènement -->
              <div class="col-md-6 col-sm-12 mb-4">
                <div class="bold-text">
                  <show-array-value-field
                    :object="project"
                    :property="'projectTypes'"
                    :display-label="false">
                  </show-array-value-field>
                </div>
              </div>

              <!-- commission -->
              <div class="col-md-6 col-sm-12 mb-4 text-right">
                <div class="bold-text">
                  <show-value-field
                    :object="project.commission"
                    :property="'tostring'"
                    :display-label="false">
                  </show-value-field>
                </div>
              </div>

              <!-- titre -->
              <div class="col-md-12 mb-4 text-center">
                <div class="title mb-2"> {{ project.tostring }} </div>
                <div class="bold-text mb-2"> {{ project.dateString}} </div>
                <div> {{project.isFree? $t('project.freeAccess'): $t('project.notFreeAccess')}} - {{ project.projectAccess? $t(project.projectAccess.text): '' }} - {{project.projectPublic? $t(project.projectPublic.text + '.message'): ''}} </div>
              </div>

              <!-- description -->
              <div class="col-md-12 mb-4">
                <show-rich-text-value-field
                  :object="project"
                  :property="'description'"
                  :display-label="false">
                </show-rich-text-value-field>
                <!-- prix -->
                <show-value-field
                  v-if="project && !project.isFree"
                  :object="project"
                  :property="'priceDetails'"
                  :display-label="false">
                </show-value-field>
              </div>

              <!-- lieu de l'évènement -->
              <div class="col-md-6 col-sm-12 pb-4">
                <div class="section p-3 mb-4">
                  <div class="row">
                    <div class="col-md-12 section-title mb-4">{{$t('project.section.Place')}}</div>
                    <!-- nom du lieu -->
                    <div class="col-md-12 mb-4 bold-text">
                      <show-value-field
                        :object="project.projectPlace"
                        :property="'tostring'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- adresse du lieu -->
                    <div class="col-md-12 mb-4">
                      <show-value-field
                        :object="project.projectPlace"
                        :property="'address'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- téléphone du lieu -->
                    <div class="col-md-12 mb-4">
                      <show-value-field
                        :object="project.projectPlace"
                        :property="'phoneNumber'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- site internet du lieu -->
                    <div class="col-md-12">
                      <show-link-value-field
                        :object="project.projectPlace"
                        :property="'website'"
                        :display-label="false">
                      </show-link-value-field>
                    </div>
                  </div>
                </div>
              </div>

              <!-- organisateur de l'évènement -->
              <div class="col-md-6 col-sm-12 pb-4">
                <div class="section p-3 mb-4">
                  <div class="row">
                    <div class="col-md-12 section-title mb-4">{{$t('project.section.Organizer')}}</div>
                    <!-- nom de l'organisateur -->
                    <div class="col-md-12 mb-4 bold-text">
                      <show-value-field
                        :object="project.projectOrganizer"
                        :property="'tostring'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- type de l'organisateur -->
                    <div class="col-md-12 mb-4">
                      <show-value-field
                        :object="project.projectOrganizer"
                        :property="'organizerType'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- nom du contact de l'organisateur -->
                    <div class="col-md-12 mb-4">
                      <show-value-field
                        :object="project.projectOrganizer"
                        :property="'contactFullName'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- téléphone du contact de l'organisateur -->
                    <div class="col-md-12 mb-4">
                      <show-value-field
                        :object="project.projectOrganizer"
                        :property="'contactPhoneNumber'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- email du contact de l'organisateur -->
                    <div class="col-md-12 mb-4">
                      <show-value-field
                        :object="project.projectOrganizer"
                        :property="'contactEmail'"
                        :display-label="false">
                      </show-value-field>
                    </div>
                    <!-- site internet de la structure -->
                    <div class="col-md-12">
                      <show-link-value-field
                        :object="project.projectOrganizer"
                        :property="'website'"
                        :display-label="false">
                      </show-link-value-field>
                    </div>
                  </div>
                </div>
              </div>

              <!-- dates de l'évènement -->
              <div class="col-md-6 col-sm-12 pb-4">
                <div class="section p-3">
                  <div class="row">
                    <div class="col-md-12 section-title mb-4">{{$t('project.section.Dates')}}</div>
                    <ul>
                      <template v-for="(projectDate) in project.projectDates">
                        <li>{{ projectDate.dateString }}</li>
                      </template>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- réseaux sociaux de l'évènement -->
              <div class="col-md-6 col-sm-12 pb-4">
                <div class="section p-3 mb-4">
                  <div class="row">
                    <div class="col-md-12 section-title mb-4">{{$t('project.section.Network')}}</div>
                    <div class="col-md-12 d-flex flex-row " style="max-height: 60px; gap: 10px;">
                      <!-- site internet de l'évènement -->
                      <b-button class="text-decoration-none align-items-center flex-grow-1" variant="primary" :href="project.projectLink" target="_blank" v-if="project && project.projectLink">
                        {{ $t('project.ProjectLink.short') }}
                      </b-button>
                      <!-- lien facebook -->
                      <b-link :href="project.facebookLink" target="_blank" v-if="project && project.facebookLink">
                        <img :src="require(`@/assets/images/fb.png`)" alt="Logo" height="100%"/>
                      </b-link>
                      <!-- lien instagram -->
                      <b-link :href="project.instagramLink" target="_blank" v-if="project && project.instagramLink">
                        <img :src="require(`@/assets/images/insta.png`)" alt="Logo" height="100%"/>
                      </b-link>
                    </div>
                  </div>
                </div>
              </div>

              <!-- map -->
              <div class="col-md-12 mb-4" v-if="project.projectPlace? project.projectPlace.address: null">
                <show-address-map
                  :display-label="false"
                  :latitude="project.projectPlace.latitude"
                  :longitude="project.projectPlace.longitude">
                </show-address-map>
              </div>

              <!-- commentaire createur -->
              <div class="col-md-12" v-if="project.userComment">
                <div class="row">
                  <div class="col-md-12 section-title">{{$t('project.UserComment')}}</div>
                  <div class="col-md-12">
                    <show-value-field
                      :object="project"
                      :property="'userComment'"
                      :display-label="false">
                    </show-value-field>
                  </div>
                </div>
              </div>

              </div>
          </b-card>
        </div>

      </div>

    </b-container>

    <!-- Modal Delete -->
    <Item-delete-modal
      :itemSingularName="'project'"
      :beginsWithVoyel="true"
      :selectedItem="project"
      :selectedItemSpecialString="project ? project.tostring : null"
      controllerName="project"
      itemSingularNameUpp="Project"
      @didDeleteItem="didDeleteProject()">
    </Item-delete-modal>

  </div>
</template>

<script>

import api from '../../services/api'
import BackArrow from '../../components/b122/BackArrow'
import ShowActions from '../../components/b122/ShowActions'
import ItemDeleteModal from "../../components/b122/ItemDeleteModal.vue"
import ProjectStatusAlertComponent from "./components/ProjectStatusAlertComponent.vue"
import ShowImageField from "../../components/b122/showFields/showImageField.vue"
import ShowValueField from "../../components/b122/showFields/ShowValueField.vue"
import ShowArrayValueField from "../../components/b122/showFields/ShowArrayValueField.vue"
import ShowLinkValueField from "../../components/b122/showFields/ShowLinkValueField.vue"
import ShowRichTextValueField from "../../components/b122/showFields/ShowRichTextValueField.vue"
import ShowAddressMap from "../../components/b122/showFields/ShowAddressMap.vue"
import ShowObjectLinkValueField from "../../components/b122/showFields/ShowObjectLinkValueField.vue"
import ShowDateField from "../../components/b122/showFields/ShowDateField.vue"
import ShowBooleanField from "../../components/b122/showFields/ShowBooleanField.vue"

export default {

  name: 'ProjectShow',

  components: {
    ShowBooleanField,
    ShowDateField,
    ShowObjectLinkValueField,
    ShowAddressMap,
    ShowRichTextValueField,
    ShowLinkValueField,
    ShowArrayValueField,
    ShowValueField,
    ShowImageField,
    ProjectStatusAlertComponent,
    ItemDeleteModal,
    BackArrow,
    ShowActions,
  },

  data () {
    return {
      allowDuplicate: this.$store.getters.isUser,
      allowBoost: false,
      allowUnboost: false,
      allowDelete: true,
      allowDeleteForever: false,
      allowEdit: false,
      projectId: this.$route.params.projectId,
      project: {},
      showModifiedFields: false,
      isLoadingRefuse: false,
      isLoadingAccept: false
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    fetchData () {
      this.fetchProject()
    },

    fetchProject () {
      api
        .get(`/project/${this.projectId}`)
        .then(response => {
          this.project = response.data
          this.initValues()
        })
        .catch((error) => {
          this.$router.push({
            name: 'ProjectIndex',
            params: {
              message: error.response.data.message
            }
          })
        })
    },

    initValues() {
      this.allowEdit = this.project.projectStatus.value !== 'REFUSED'
      this.allowDelete = this.project.projectStatus.value !== 'DELETED'
      this.allowDeleteForever = this.project.projectStatus.value === 'DELETED'
      this.showModifiedFields = this.project.projectStatus.value === 'CERTIFIED_PUBLISHED_MODIFIED'
      if (!this.$store.getters.isUser) {
        this.allowBoost = !this.project.boosted && this.project.projectStatus.value === 'CERTIFIED_PUBLISHED'
        this.allowUnboost = this.project.boosted && this.project.projectStatus.value === 'CERTIFIED_PUBLISHED'
      }
    },

    boostProject () {
      api
        .post(`/project/boostProject/${this.projectId}`)
        .then(response => {
          this.project = response.data
          this.allowBoost = !this.$store.getters.isUser && !this.project.boosted && this.project.projectStatus.value === 'CERTIFIED_PUBLISHED'
          this.allowUnboost = !this.$store.getters.isUser && this.project.boosted && this.project.projectStatus.value === 'CERTIFIED_PUBLISHED'
        })
    },

    acceptModifications () {
      this.isLoadingAccept = true
      api
        .post(`/project/acceptModifications/${this.projectId}`)
        .then(response => {
          this.project = response.data
          this.initValues()
          this.isLoadingAccept = false
        })
        .catch(() => {
          this.isLoadingAccept = false
        })
    },

    refuseModifications () {
      this.isLoadingRefuse = true
      api
        .post(`/project/refuseModifications/${this.projectId}`)
        .then(response => {
          this.project = response.data
          this.initValues()
          this.isLoadingRefuse = false
        })
        .catch(() => {
          this.isLoadingRefuse = false
        })
    },

    clickOnDuplicateBtn () {
      this.$router.push({
        name: 'ProjectCreate',
        params: {
          duplicateProjectId: this.project.id
        }
      })
    },

    clickOnBoostBtn () {
      this.boostProject()
    },

    clickOnEditBtn () {
      this.$router.push({
        name: 'ProjectEdit',
        params: {
          projectId: this.project.id
        }
      })
    },

    clickOnDeleteBtn () {
      this.$bvModal.show('item-delete-modal')
    },

    didDeleteProject () {
      this.$router.push({
        name: 'ProjectIndex'
      })
    },

    successToast (message) {
      this.$bvToast.toast(message, {
        autoHideDelay: 2000,
        toaster: 'b-toaster-bottom-right',
        noCloseButton: true
      })
    }

  }
}
</script>

<style scoped>

.title {
  font-family: "SourceSerif4-Regular", "Arial", sans-serif;
  font-size: var(--title-level-one-font-size);
}

.section {
  height: 100%;
}

</style>



// WEBPACK FOOTER //
// src/views/project/ProjectShow.vue